/** Procurement Products lens (Demo Mode only) - shows supplier products across catalogues
 * from a procurement point of view (supplier, catalogue, availability, approval and data
 * quality status, recipe usage), as an additional view alongside the existing (real,
 * backend-driven) Products list on this page. Entirely mock-data driven, no backend calls -
 * see src/mocks/data/procurementProductsLensData.ts. Clicking a row opens a read-only detail
 * dialog; from there the user can jump to the real Products list (filtered by product code)
 * to open the real product edit screen. */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { Pill } from '../../../../components/shared/Pill';
import CustomPagination from '../../../../components/forms/Pagination';
import { EmptyRows } from '../../../../components/forms/EmptyRows';
import {
  AvailabilityChip,
  ApprovalStatusChip,
  DataQualityChip,
} from '../../../../components/shared/procurementLens/StatusChips';
import {
  ProcurementLensProduct,
  ProcurementRecipeUsage,
  getProcurementLensFilterOptions,
  procurementLensProducts,
} from '../../../../mocks/data/procurementProductsLensData';

const ALL = 'All';
const PAGE_SIZE = 10;

const ProcurementProductsView = () => {
  const { t } = useTranslation('productManagement');
  const navigate = useNavigate();
  const options = getProcurementLensFilterOptions();

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState(ALL);
  const [catalogueFilter, setCatalogueFilter] = useState(ALL);
  const [categoryFilter, setCategoryFilter] = useState(ALL);
  const [availabilityFilter, setAvailabilityFilter] = useState(ALL);
  const [approvalFilter, setApprovalFilter] = useState(ALL);
  const [dataQualityFilter, setDataQualityFilter] = useState(ALL);
  const [recipeUsageFilter, setRecipeUsageFilter] = useState(ALL);
  const [page, setPage] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<ProcurementLensProduct | null>(null);

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSupplierFilter(ALL);
    setCatalogueFilter(ALL);
    setCategoryFilter(ALL);
    setAvailabilityFilter(ALL);
    setApprovalFilter(ALL);
    setDataQualityFilter(ALL);
    setRecipeUsageFilter(ALL);
    setPage(0);
  };

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return procurementLensProducts.filter((product) => {
      if (term && !product.productCode.toLowerCase().includes(term) && !product.productName.toLowerCase().includes(term)) return false;
      if (supplierFilter !== ALL && product.supplierName !== supplierFilter) return false;
      if (catalogueFilter !== ALL && product.catalogueName !== catalogueFilter) return false;
      if (categoryFilter !== ALL && product.category !== categoryFilter) return false;
      if (availabilityFilter !== ALL && product.availability !== availabilityFilter) return false;
      if (approvalFilter !== ALL && product.approvalStatus !== approvalFilter) return false;
      if (dataQualityFilter !== ALL && product.dataQualityStatus !== dataQualityFilter) return false;
      if (recipeUsageFilter !== ALL && product.recipeUsage !== recipeUsageFilter) return false;
      return true;
    });
  }, [searchTerm, supplierFilter, catalogueFilter, categoryFilter, availabilityFilter, approvalFilter, dataQualityFilter, recipeUsageFilter]);

  const maxPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const pagedProducts = filteredProducts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(0);
  };

  const handleOpenInProductsList = (productCode: string) => {
    setSelectedProduct(null);
    navigate(`/procurement/products?searchTerm=${encodeURIComponent(productCode)}`);
  };

  return (
    <>
      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mb: 4 }}>
        {t(
          'procurementViewIntro',
          'Supplier products across catalogues, viewed from a procurement lens - availability, approval and data quality status, and recipe usage.',
        )}
      </Typography>

      <Grid container
        spacing={5}
        sx={{ mb: 4 }}>
        <Grid item
          lg={4}
          sm={6}
          xs={12}>
          <TextField
            label={t('search')}
            placeholder={t('procurementSearchPlaceholder', 'Search by product code or name')}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyPress={(event) => { if (event.key === 'Enter') handleSearch(); }}
            fullWidth
            sx={{ mb: 0 }}
          />
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={() => { setSearchInput(''); setSearchTerm(''); setPage(0); }}
            variant="secondary"
            size="lg"
          >
            {t('clear', 'Clear')}
          </Button>
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={handleSearch}
            size="lg"
          >
            {t('search', 'Search')}
          </Button>
        </Grid>
      </Grid>

      <Accordion
        variant="borderless"
        expanded={isMoreFiltersOpen}
        onChange={(_event, expanded) => setIsMoreFiltersOpen(expanded)}
        sx={{ mb: 4 }}
      >
        <AccordionSummary>
          <Grid container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}>
            <Grid item>
              <Grid container
                spacing={1}
                alignItems="center">
                <Grid item>
                  {t('advancedFilters', 'Advanced Filters')}
                </Grid>
                <Grid item
                  style={{ marginTop: '8px' }}>
                  {isMoreFiltersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                size="md"
                variant="secondary"
                onClick={(event) => { event.stopPropagation(); handleClearFilters(); }}
              >
                {t('clearFilters', 'Clear Filters')}
              </Button>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container
            spacing={3}>
            <Grid item
              md={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                label={t('filterBySupplier', 'Supplier')}
                value={supplierFilter}
                onChange={(event) => { setSupplierFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value={ALL}>{t('allSuppliers', 'All suppliers')}</MenuItem>
                {options.suppliers.map((supplier) => (
                  <MenuItem key={supplier}
                    value={supplier}>{supplier}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              md={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                label={t('filterByCatalogue', 'Catalogue')}
                value={catalogueFilter}
                onChange={(event) => { setCatalogueFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value={ALL}>{t('allCatalogues', 'All catalogues')}</MenuItem>
                {options.catalogues.map((catalogue) => (
                  <MenuItem key={catalogue}
                    value={catalogue}>{catalogue}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              md={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                label={t('filterByCategory', 'Category')}
                value={categoryFilter}
                onChange={(event) => { setCategoryFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value={ALL}>{t('allCategories', 'All categories')}</MenuItem>
                {options.categories.map((category) => (
                  <MenuItem key={category}
                    value={category}>{category}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              md={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                label={t('filterByAvailability', 'Availability')}
                value={availabilityFilter}
                onChange={(event) => { setAvailabilityFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value={ALL}>{t('allAvailability', 'All availability')}</MenuItem>
                {options.availabilities.map((availability) => (
                  <MenuItem key={availability}
                    value={availability}>{availability}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              md={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                label={t('filterByApprovalStatus', 'Approval status')}
                value={approvalFilter}
                onChange={(event) => { setApprovalFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value={ALL}>{t('allApprovalStatuses', 'All approval statuses')}</MenuItem>
                {options.approvalStatuses.map((status) => (
                  <MenuItem key={status}
                    value={status}>{status}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              md={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                label={t('filterByDataQualityStatus', 'Data quality status')}
                value={dataQualityFilter}
                onChange={(event) => { setDataQualityFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value={ALL}>{t('allDataQualityStatuses', 'All data quality statuses')}</MenuItem>
                {options.dataQualityStatuses.map((status) => (
                  <MenuItem key={status}
                    value={status}>{status}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              md={3}
              sm={6}
              xs={12}>
              <TextField
                select
                fullWidth
                label={t('filterByRecipeUsage', 'Recipe usage')}
                value={recipeUsageFilter}
                onChange={(event) => { setRecipeUsageFilter(event.target.value); setPage(0); }}
              >
                <MenuItem value={ALL}>{t('allRecipeUsage', 'All recipe usage')}</MenuItem>
                {options.recipeUsages.map((usage) => (
                  <MenuItem key={usage}
                    value={usage}>{usage === ProcurementRecipeUsage.UsedInRecipes ? t('usedInRecipes', 'Used in recipes') : t('notUsedInRecipes', 'Not used in recipes')}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Section>
        <Box sx={{ p: 5, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography variant="textMD"
              fontWeight={600}>
              {t('procurementView', 'Procurement view')}
            </Typography>
            {filteredProducts.length > 0 && <Pill text={`${filteredProducts.length} ${t('products')}`} />}
          </Box>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: '1600px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('productCode', 'Product Code')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('productName', 'Product Name')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('supplier', 'Supplier')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('catalogue', 'Catalogue')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('category')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('packSize')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('orderableUom')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('baseUom', 'Base UOM')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('price', 'Price')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('availability', 'Availability')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('approvalStatus', 'Approval status')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('dataQualityStatus', 'Data quality status')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('recipeUsage', 'Recipe usage')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedProducts.map((product) => (
                <TableRow
                  key={product.productCode}
                  hover
                  onClick={() => setSelectedProduct(product)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{product.productCode}</TableCell>
                  <TableCell>{product.productName}</TableCell>
                  <TableCell>{product.supplierName}</TableCell>
                  <TableCell>{product.catalogueName}</TableCell>
                  <TableCell>{product.category}</TableCell>
                  <TableCell>{product.packSize}</TableCell>
                  <TableCell>{product.orderableUom}</TableCell>
                  <TableCell>{product.baseUom}</TableCell>
                  <TableCell>{product.price.toFixed(2)}</TableCell>
                  <TableCell><AvailabilityChip status={product.availability} /></TableCell>
                  <TableCell><ApprovalStatusChip status={product.approvalStatus} /></TableCell>
                  <TableCell><DataQualityChip status={product.dataQualityStatus} /></TableCell>
                  <TableCell>
                    {product.recipeUsage === ProcurementRecipeUsage.UsedInRecipes
                      ? t('usedInRecipesCount', '{{count}} recipes', { count: product.recipeUsageCount })
                      : t('notUsedInRecipes', 'Not used in recipes')}
                  </TableCell>
                </TableRow>
              ))}
              <EmptyRows numberOfCurrentRows={pagedProducts.length}
                height={40}
                colSpan={13} />
            </TableBody>
          </Table>
        </TableContainer>
        <CustomPagination
          page={page}
          setPage={setPage}
          maxPages={maxPages}
          breakpointForChangeDisplay={120000}
          useNoDataMessage={filteredProducts.length === 0}
        />
      </Section>

      <Dialog
        open={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedProduct && (
          <>
            <DialogTitle>{selectedProduct.productName}</DialogTitle>
            <DialogContent>
              <Grid container
                spacing={2}
                sx={{ mt: 0 }}>
                <DetailField label={t('productCode', 'Product Code')}
                  value={selectedProduct.productCode} />
                <DetailField label={t('supplier', 'Supplier')}
                  value={selectedProduct.supplierName} />
                <DetailField label={t('catalogue', 'Catalogue')}
                  value={selectedProduct.catalogueName} />
                <DetailField label={t('category')}
                  value={selectedProduct.category} />
                <DetailField label={t('packSize')}
                  value={selectedProduct.packSize} />
                <DetailField label={t('orderableUom')}
                  value={selectedProduct.orderableUom} />
                <DetailField label={t('baseUom', 'Base UOM')}
                  value={selectedProduct.baseUom} />
                <DetailField label={t('price', 'Price')}
                  value={selectedProduct.price.toFixed(2)} />
                <Grid item
                  xs={6}>
                  <Typography variant="textXS"
                    color="text.secondary">{t('availability', 'Availability')}</Typography>
                  <AvailabilityChip status={selectedProduct.availability} />
                </Grid>
                <Grid item
                  xs={6}>
                  <Typography variant="textXS"
                    color="text.secondary">{t('approvalStatus', 'Approval status')}</Typography>
                  <ApprovalStatusChip status={selectedProduct.approvalStatus} />
                </Grid>
                <Grid item
                  xs={6}>
                  <Typography variant="textXS"
                    color="text.secondary">{t('dataQualityStatus', 'Data quality status')}</Typography>
                  <DataQualityChip status={selectedProduct.dataQualityStatus} />
                </Grid>
                <DetailField label={t('recipeUsage', 'Recipe usage')}
                  value={
                    selectedProduct.recipeUsage === ProcurementRecipeUsage.UsedInRecipes
                      ? t('usedInRecipesCount', '{{count}} recipes', { count: selectedProduct.recipeUsageCount })
                      : t('notUsedInRecipes', 'Not used in recipes')
                  } />
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                variant="secondary"
                onClick={() => setSelectedProduct(null)}
              >
                {t('close', 'Close')}
              </Button>
              <Button
                variant="primary"
                onClick={() => handleOpenInProductsList(selectedProduct.productCode)}
              >
                {t('openInProductsList', 'Open in Products list')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const DetailField = ({ label, value }: { label: string; value: string }) => (
  <Grid item
    xs={6}>
    <Typography variant="textXS"
      color="text.secondary">{label}</Typography>
    <Typography variant="textSM">{value}</Typography>
  </Grid>
);

export default ProcurementProductsView;
